import React from 'react';
import { PredictProvider } from '~/providers/PredictProvider';
import { TransitionProvider } from '~/providers/TransitionProvider';
import { useNotifications } from '~/providers/NotificationProvider';
import { useTransitionAttention } from '~/providers/TransitionAttentionProvider';
import { useIntakeQueue } from '~/providers/IntakeQueueProvider';
import { ReferralPatient } from '~/types/transition';
import { IntakePatient } from '~/features/capacity-management/types/intake.types';

function cityToTerritory(city: string): string {
  const lower = city.toLowerCase().trim();
  if (['aurora', 'centennial', 'parker'].includes(lower)) return 'East';
  if (['boulder', 'broomfield', 'westminster', 'northglenn', 'longmont'].includes(lower)) return 'North';
  if (['littleton', 'highlands ranch', 'castle rock', 'englewood'].includes(lower)) return 'South';
  if (['lakewood', 'arvada', 'golden', 'wheat ridge'].includes(lower)) return 'West';
  return 'North';
}

function referralPatientToIntakePatient(referralId: string, patient: ReferralPatient): IntakePatient {
  const nameParts = patient.name.trim().split(' ');
  const lastName = nameParts[nameParts.length - 1];
  const firstName = nameParts.slice(0, -1).join(' ');
  const now = new Date();
  const intakeDate = new Date(now);
  intakeDate.setDate(intakeDate.getDate() + 2);
  return {
    id: Date.now() + Math.floor(Math.random() * 9999),
    patientName: `${lastName}, ${firstName}`,
    expectedDischargeDate: now,
    intakeVisitDate: intakeDate,
    requiredRole: 'RN',
    priority: 'HIGH',
    expectedVisitsPerWeek: 3,
    expectedDurationWeeks: 4,
    territory: cityToTerritory(patient.city),
    status: 'unassigned',
  };
}

export const AppProviderBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showNotification, showPatientChangeNotification } = useNotifications();
  const { addUnseen } = useTransitionAttention();
  const { addToQueue } = useIntakeQueue();

  const handleTransitionNotify: React.ComponentProps<typeof TransitionProvider>['onNotify'] = (referralId, params) => {
    addUnseen(referralId);
    if (params.showToast !== false) {
      showNotification({
        type: params.type,
        title: params.title,
        message: params.message,
        referralId,
        autoHideDuration: 5000,
      });
    }
  };

  const handleReferralAccepted = (referralId: string, patient: ReferralPatient) => {
    addToQueue(referralId, referralPatientToIntakePatient(referralId, patient));
  };

  return (
    <PredictProvider onPatientChange={showPatientChangeNotification}>
      <TransitionProvider onNotify={handleTransitionNotify} onReferralAccepted={handleReferralAccepted}>
        {children}
      </TransitionProvider>
    </PredictProvider>
  );
};
